import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import type { Id } from '../api/types';
import { useQltTimetableStore } from '../stores/qlt';
import { periodLabel, resolvePeriodLabels } from './periodLabels';
import { useLanguage } from '../i18n/msg';

export interface BoardNavigationState {
  classTitle?: string;
  subjectTitle?: string;
  lessonTitle?: string;
  topicLessonId?: string;
  classNoteId?: Id;
  subjectId?: Id;
  gradeId?: Id;
  courseId?: string;
  /** The period's planned lesson, offered under Đề xuất; the board itself opens blank. */
  suggestedUnitId?: string;
  suggestedUnitName?: string;
  suggestedCourseId?: string;
  /** Set when the teacher deliberately chose Bảng trắng - see useClearUnit. */
  blankBoard?: boolean;
}

export interface BoardContextValue extends BoardNavigationState {
  /** Always resolved; '' when neither route state nor the timetable names it. */
  classTitle: string;
  subjectTitle: string;
  date: string;
  index: number;
  classId: string;
  unitId: string | null;
  /** "Tiết 3-4" for a merged block; per-period data still keys on index. */
  periodLabel: string;
}

export function useBoardContext(): BoardContextValue {
  const params = useParams();
  const [search] = useSearchParams();
  const location = useLocation();
  const state = (location.state || {}) as BoardNavigationState;
  // periodLabel below is worded here, so a language switch must re-render whoever reads the context
  useLanguage();
  const cells = useQltTimetableStore((store) => store.todayCells);
  const date = params.date || '';
  const index = Number(params.index) || 0;
  const classId = params.classId || '';
  // a board loaded by URL has no route state; its timetable cell still names the subject and class
  const labels = resolvePeriodLabels(state, cells, { date, index, classId });
  return {
    date,
    index,
    classId,
    unitId: search.get('unit'),
    periodLabel: periodLabel(cells, { date, index, classId }),
    classTitle: labels.classTitle,
    subjectTitle: labels.subjectTitle,
    lessonTitle: state.lessonTitle,
    topicLessonId: state.topicLessonId,
    classNoteId: state.classNoteId,
    subjectId: state.subjectId,
    gradeId: state.gradeId,
    courseId: state.courseId,
    blankBoard: state.blankBoard,
    suggestedUnitId: state.suggestedUnitId,
    suggestedUnitName: state.suggestedUnitName,
    suggestedCourseId: state.suggestedCourseId,
  };
}
